
import React, { useState, useEffect, useRef } from 'react';
import { resizeImage } from '../../utils/imageHelper';
import { uploadImageBase64 } from './maintenanceService';
import CameraCaptureModal from './CameraCaptureModal';

interface ImageUploaderProps {
  maintenanceId: number;
  onUploadComplete: () => void;
  userId: string;
}

const MAX_FILES = 3;

const ImageUploader: React.FC<ImageUploaderProps> = ({ maintenanceId, onUploadComplete, userId }) => {
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Clean up object URLs to prevent memory leaks when the component unmounts.
    return () => {
      previews.forEach(URL.revokeObjectURL);
    };
  }, [previews]);
  
  /**
   * A shared function to handle adding new files from any source (upload, drop, camera).
   * It performs validation for file count and type.
   * @param {File[]} newFiles - An array of files to add.
   */
  const addFiles = (newFiles: File[]) => {
    setError(null);
    if (filesToUpload.length + newFiles.length > MAX_FILES) {
      setError(`You can only upload a maximum of ${MAX_FILES} files.`);
      return;
    }

    const imageFiles = newFiles.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length !== newFiles.length) {
        setError("Please add image files only.");
        return;
    }

    setFilesToUpload(prev => [...prev, ...imageFiles]);
    const newPreviews = imageFiles.map(file => URL.createObjectURL(file));
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    addFiles(Array.from(event.target.files));
  };

  const handleRemoveFile = (index: number) => {
    setError(null);
    setFilesToUpload(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleCameraCapture = (blob: Blob) => {
    const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
    addFiles([file]);
    setIsCameraOpen(false);
  };

  const handleUpload = async () => {
    if (filesToUpload.length === 0 || isUploading) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);
    
    for (let i = 0; i < filesToUpload.length; i++) {
      try {
        const file = filesToUpload[i];
        const base64Image = await resizeImage(file);
        await uploadImageBase64(maintenanceId, base64Image, userId);
        setUploadProgress(((i + 1) / filesToUpload.length) * 100);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
        setError(`Upload failed for file ${i + 1}. Please try again. ${errorMessage}`);
        setIsUploading(false);
        return;
      }
    }

    setIsUploading(false);
    setFilesToUpload([]);
    previews.forEach(URL.revokeObjectURL); // FIX: Revoke URLs to prevent memory leak
    setPreviews([]);
    onUploadComplete();
  };
  
  // Drag and Drop Handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) {
        setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation(); // Necessary to allow drop
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    if (isUploading) return;
    
    // FIX: The type of `droppedFiles` was being inferred as `unknown[]` which caused a type error on the next line.
    // Using the spread operator `[...]` correctly converts the `FileList` from `e.dataTransfer.files` into a `File[]`.
    const droppedFiles = [...e.dataTransfer.files];
    if (droppedFiles.length > 0) {
        addFiles(droppedFiles);
    }
  };

  return (
    <div 
        className={`bg-gray-50 border rounded-lg p-4 transition-all duration-300 ${isDraggingOver ? 'border-indigo-500 border-dashed ring-2 ring-offset-2 ring-indigo-200 bg-indigo-50' : 'border-gray-200'}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
            <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || filesToUpload.length >= MAX_FILES}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                {previews.length > 0 ? 'Add More' : 'Upload Files'}
            </button>
            <input
                type="file"
                ref={fileInputRef}
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
            />
            <button
                type="button"
                onClick={() => setIsCameraOpen(true)}
                disabled={isUploading || filesToUpload.length >= MAX_FILES}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" /></svg>
                Use Camera
            </button>
        </div>
        <p className="text-xs text-gray-500 text-left sm:text-right">
            {previews.length === 0 ? "Or drag and drop files. " : ""}
            Attach up to {MAX_FILES} images.
        </p>
      </div>

      {previews.length > 0 && (
        <div className="mt-4">
          <div className="grid grid-cols-3 gap-4">
            {previews.map((preview, index) => (
              <div key={preview} className="relative group">
                <img src={preview} alt={`Preview ${index}`} className="w-full h-24 object-cover rounded-md border border-gray-300" />
                <button
                  onClick={() => handleRemoveFile(index)}
                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-0.5 hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
                  aria-label={`Remove image ${index + 1}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={handleUpload}
            disabled={isUploading}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300 disabled:cursor-not-allowed"
          >
            {isUploading ? "Uploading..." : `Upload ${filesToUpload.length} Image(s)`}
          </button>
        </div>
      )}

      {isUploading && (
        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
          <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%`, transition: 'width 0.3s ease-in-out' }}></div>
        </div>
      )}

      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      
      {isCameraOpen && (
        <CameraCaptureModal 
            onCapture={handleCameraCapture}
            onClose={() => setIsCameraOpen(false)}
        />
      )}
    </div>
  );
};

export default ImageUploader;
